/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { css, cx } from "@panda/css";

import { BlogCard, BlogCardProps, BlogCardVariant, BlurredImage } from "@components/elements";
import { Flex, VStack } from "@panda/jsx";
import { SystemStyleObject } from "@panda/types";

export type FeaturedBlogCardsProps = {
	className?: string;
	cards?: BlogCardProps[] | null;
};

const CommonCardStyles: SystemStyleObject = {
	"&&": {
		"--cosmos-card-padding": "token(spacing.eds.dimension.12)",
	},
	"&& .cosmos-card__thumbnail": {
		aspectRatio: "16/9",
	},
	"&& .blog-card-content": {
		justifyContent: "center",
		padding: "eds.dimension.8",
		"& > div": {
			gap: "eds.dimension.8",
			"&& .cosmos-date": {
				textStyle: "paragraphSM",
			},
			"&& .blog-card-title": {
				lineClamp: "2",
				overflow: "hidden",
			},
		},
	},
};

const FeaturedCardStyles: SystemStyleObject = {
	"&&": {
		minWidth: "0",
		flexBasis: "60%",
		lgOnly: {
			flexBasis: "50%",
			"&& .cosmos-card__thumbnail": {
				height: "100%",
				aspectRatio: "unset",
				flexBasis: "unset",
			},
		},
		lgDown: {
			flexBasis: "100%",
		},
	},
};

const ColumnCardStyles: SystemStyleObject = {
	"&&": {
		gap: "eds.dimension.16",
		alignSelf: "stretch",
		flexDir: "column",
		xxlDown: {
			gap: "eds.dimension.12",
		},
		"&& .blog-card-title": {
			textStyle: "headingLG",
		},
		smDown: {
			"&& .blog-card-title": {
				textStyle: "headingSM",
			},
			"--cosmos-card-padding": "token(spacing.eds.dimension.16)",
		},
	},
};

const RowCardStyles: SystemStyleObject = {
	"&&": {
		height: "100%",
		alignSelf: "stretch",
		gap: "eds.dimension.12",
		"&& .blog-card-title": {
			textStyle: "headingSM",
		},
		"&& .cosmos-card__thumbnail": {
			minWidth: "180px",
			width: "180px",
			flexBasis: "unset",
		},
	},
};

export function FeaturedBlogCards({ cards = [], className }: FeaturedBlogCardsProps): React.JSX.Element | null {
	if (!cards || !cards.length) return null;

	const length = cards.length;
	let variant: BlogCardVariant;
	if (length === 1) {
		variant = "single";
	}

	return (
		<Flex
			className={cx(
				"featured-blog-cards",
				css({
					gap: "eds.dimension.16",
					display: "flex",
					position: "relative",
					lgDown: {
						flexDirection: "column",
					},
				}),
				className,
			)}
		>
			<BlurredImage
				className={css({
					width: "100%",
					position: "absolute",
				})}
				image={cards[0].asset}
				width={900}
				height={506.25}
				opacity={0.3}
				blurPixels={75}
				disableAutoplay
			/>
			<BlogCard
				className={cx(
					"featured-blog-card",
					css(CommonCardStyles, FeaturedCardStyles, ColumnCardStyles, {
						flexGrow: "1",
						"&&": {
							flexBasis: variant === "single" ? "100%" : "70%",
						},
					}),
				)}
				{...cards[0]}
				variant={variant}
			/>
			{variant !== "single" ? (
				<VStack
					gap="eds.dimension.16"
					className={css({
						minWidth: "0",
						alignSelf: "stretch",
						justifyContent: "space-between",
						flexGrow: "1",
						flexBasis: "40%",
						lgOnly: {
							flexBasis: "50%",
						},
					})}
				>
					{(cards || []).slice(1, 5).map((card, i) => (
						<BlogCard
							key={`mini-blog-card-item-${i}`}
							className={cx("mini-blog-card", css(CommonCardStyles, { sm: RowCardStyles, smDown: ColumnCardStyles }))}
							{...card}
							direction="row"
						/>
					))}
				</VStack>
			) : null}
		</Flex>
	);
}
